import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

const allergens = [
  { id: 'peanuts', name: 'Peanuts', emoji: '🥜' },
  { id: 'treeNuts', name: 'Tree Nuts', emoji: '🌳' },
  { id: 'dairy', name: 'Dairy', emoji: '🥛' },
  { id: 'gluten', name: 'Gluten', emoji: '🍞' },
  { id: 'soy', name: 'Soy', emoji: '🌱' },
  { id: 'shellfish', name: 'Shellfish', emoji: '🦀' },
  { id: 'fish', name: 'Fish', emoji: '🐟' },
  { id: 'eggs', name: 'Eggs', emoji: '🥚' },
  { id: 'wheat', name: 'Wheat', emoji: '🌾' },
  { id: 'sesame', name: 'Sesame', emoji: '🌸' }
];

const FoodAllergenCalculator = () => {
  const [formData, setFormData] = useState({
    foodItem: '',
    selectedAllergens: new Set()
  });
  const [results, setResults] = useState(null);

  const handleAllergenChange = (id) => {
    setFormData(prev => {
      const newSelected = new Set(prev.selectedAllergens);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return { ...prev, selectedAllergens: newSelected };
    });
  };

  const checkAllergens = () => {
    const { foodItem, selectedAllergens } = formData;
    
    if (!foodItem || selectedAllergens.size === 0) {
      alert('Please enter a food item and select at least one allergen!');
      return;
    }

    // Convert food item to lowercase for better matching
    const foodLower = foodItem.toLowerCase();

    // Simulate allergen check with more accurate detection
    const detectedAllergens = Array.from(selectedAllergens).reduce((acc, id) => {
      const allergen = allergens.find(a => a.id === id);
      if (allergen) {
        const isDetected = detectAllergen(foodLower, allergen);
        acc.push({
          name: allergen.name,
          emoji: allergen.emoji,
          detected: isDetected,
          detectionReason: isDetected ? getDetectionReason(foodLower, allergen) : null
        });
      }
      return acc;
    }, []);

    setResults(detectedAllergens);
  };

  const detectAllergen = (food, allergen) => {
    const foodWords = food.split(/\s+/).map(word => word.toLowerCase());
    const allergenKeywords = getKeywordsForAllergen(allergen);
    
    return allergenKeywords.some(keyword => 
      foodWords.some(word => word.includes(keyword))
    );
  };

  const getKeywordsForAllergen = (allergen) => {
    const keywords = {
      'peanuts': ['peanut', 'groundnut', 'peanuts'],
      'treeNuts': ['almond', 'walnut', 'cashew', 'pistachio', 'hazelnut', 'macadamia'],
      'dairy': ['milk', 'dairy', 'cheese', 'yogurt', 'cream', 'butter'],
      'gluten': ['wheat', 'gluten', 'barley', 'rye', 'spelt', 'kamut'],
      'soy': ['soy', 'tofu', 'tempeh', 'edamame'],
      'shellfish': ['shrimp', 'prawn', 'crab', 'lobster', 'shellfish'],
      'fish': ['fish', 'salmon', 'tuna', 'cod', 'trout'],
      'eggs': ['egg', 'eggs', 'albumin', 'yolk'],
      'wheat': ['wheat', 'flour', 'semolina', 'bran'],
      'sesame': ['sesame', 'tahini', 'gomasio']
    };
    
    return keywords[allergen.id] || [];
  };

  const getDetectionReason = (food, allergen) => {
    const keywords = getKeywordsForAllergen(allergen);
    const foodWords = food.split(/\s+/).map(word => word.toLowerCase());
    
    const matchedKeywords = keywords.filter(keyword => 
      foodWords.some(word => word.includes(keyword))
    );
    
    return matchedKeywords.length > 0 
      ? `Detected due to presence of: ${matchedKeywords.join(', ')}`
      : null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-yellow">
      <Helmet>
        <title>Food Allergen Calculator - Identify Food Allergens</title>
        <meta name="description" content="Identify and track food allergens in your meals. Check for common allergens like peanuts, dairy, gluten, and more. Get instant results and detailed analysis." />
        <meta property="og:title" content="Food Allergen Calculator" />
        <meta property="og:description" content="Identify and track food allergens in your meals. Check for common allergens like peanuts, dairy, gluten, and more. Get instant results and detailed analysis." />
        <meta property="og:image" content="https://your-image-url.com/food-allergen-calculator.jpg" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-black p-4 rounded-t-lg">
            <h1 className="text-3xl font-bold flex items-center">
              🍽️ Food Allergen Calculator
            </h1>
          </div>

          <div className="p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🍽️ Food Item
                </label>
                <input
                  type="text"
                  name="foodItem"
                  value={formData.foodItem}
                  onChange={(e) => setFormData({ ...formData, foodItem: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter food item name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🚫 Common Allergens
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allergens.map(allergen => (
                    <div key={allergen.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.selectedAllergens.has(allergen.id)}
                        onChange={() => handleAllergenChange(allergen.id)}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <div className="flex items-center space-x-1">
                        <span className="text-xl">{allergen.emoji}</span>
                        <span>{allergen.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={checkAllergens}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 text-green py-3 px-4 rounded-md hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center justify-center">
                  🧪 Check for Allergens
                </span>
              </button>
            </div>

            {results && (
              <div className="mt-8">
                <div className="bg-pink rounded-lg shadow-lg p-6 mb-8">
                  <h2 className="text-2xl font-bold text-green-600 mb-4">Allergen Detection Results</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Food Item Details</h3>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
                            <span className="text-green-600">🍽️</span>
                          </div>
                          <div>
                            <p className="font-medium">Food Item</p>
                            <p className="text-sm text-gray-600">{formData.foodItem}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Allergen Status</h3>
                      <div className="space-y-2">
                        {results.map((allergen, index) => (
                          <div key={index} className="flex items-center">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3">
                              <span className="text-2xl">{allergen.emoji}</span>
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{allergen.name}</p>
                              <p className="text-sm text-gray-600">
                                {allergen.detected ? '⚠️ Detected' : '✅ Safe'}
                              </p>
                              {allergen.detected && (
                                <p className="text-sm text-gray-600">{allergen.detectionReason}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">Detection Summary</h3>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <span className="text-green-600 mr-2">✅</span>
                        <div>
                          <p className="font-medium">Total Allergens Checked:</p>
                          <p className="text-sm text-gray-600">{results.length} allergens</p>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <span className="text-red-600 mr-2">⚠️</span>
                        <div>
                          <p className="font-medium">Detected Allergens:</p>
                          <p className="text-sm text-gray-600">
                            {results.filter(a => a.detected).length} detected
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <span className="text-green-600 mr-2">✅</span>
                        <div>
                          <p className="font-medium">Safe Allergens:</p>
                          <p className="text-sm text-gray-600">
                            {results.filter(a => !a.detected).length} safe
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-green-600 mb-4">How to Use</h3>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">1.</span>
                <p>Enter the name of the food item you want to check</p>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">2.</span>
                <p>Select the allergens you want to check for</p>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">3.</span>
                <p>Click "Check for Allergens" to see the results</p>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">4.</span>
                <p>Review the detection results</p>
              </li>
            </ol>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-green-600 mb-4">features & Benefits</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✅</span>
                <p>Quick and easy allergen detection</p>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✅</span>
                <p>Detailed analysis of allergen presence</p>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✅</span>
                <p>Helps in meal planning for allergies</p>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-2xl font-bold text-green-600 mb-4">FAQs</h2>
          <div className="bg-white rounded-lg shadow-lg">
            <div className="border-b">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-green-600 mb-2">What allergens are checked?</h3>
                <p>We check for common allergens including peanuts, tree nuts, dairy, gluten, soy, shellfish, fish, eggs, wheat, and sesame.</p>
              </div>
            </div>
            <div className="border-b">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-green-600 mb-2">How accurate are the results?</h3>
                <p>The results are based on known allergen patterns and should be used as a guide. Always consult a healthcare professional for medical advice.</p>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-green-600 mb-2">Can I check multiple food items at once?</h3>
              <p>Currently, you can check one food item at a time. For multiple items, repeat the process for each item.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodAllergenCalculator;
